import React, { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { useAppSettings } from '../hooks/useAppSettings'
import { formatRelativeTime as fmtRelative, formatDate as fmtDate, formatDateTime as fmtDateTime } from '../utils/time'
import PaywallModal from '../components/PaywallModal'
import { TypeSelectorModal, CreateAnnouncementModal } from '../components/AnnouncementModals'
import { collection, query, where, orderBy, getDocs, getDoc, updateDoc, doc } from 'firebase/firestore'
import { db } from '../utils/firebase'
import { renderAnnouncementContent } from '../utils/announcementContent'
import './AnnouncementsPage.css'
import {
  Megaphone,
  Pin,
  Plus,
  BarChart3,
  Check,
  ChevronDown,
  ChevronUp,
  Clock,
  Lock,
} from 'lucide-react'

function AnnouncementsPage() {
  const { userProfile, isSubscriptionActive } = useAuth()
  const isAdmin = userProfile?.isAdmin || userProfile?.isBoardMember || userProfile?.isSuperAdmin
  const canPost = !!(userProfile?.isBoardMember || userProfile?.isAdmin || userProfile?.isSuperAdmin)
  const [announcements, setAnnouncements] = useState([])
  const [loading, setLoading] = useState(true)
  const [neighborhoodName, setNeighborhoodName] = useState('')
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showTypeSelector, setShowTypeSelector] = useState(false)
  const [createType, setCreateType] = useState(null)
  // email -> profilePhotoURL, hydrated from a users query so avatars
  // on each announcement card render the author's real photo rather
  // than an initial. Announcement docs store `authorId` as email.
  const [authorPhotos, setAuthorPhotos] = useState({})

  useEffect(() => {
    if (userProfile?.neighborhoodId) {
      loadAnnouncements()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userProfile?.neighborhoodId])

  const loadAnnouncements = async () => {
    try {
      const neighborhoodDoc = await getDoc(doc(db, 'neighborhoods', userProfile.neighborhoodId))
      if (neighborhoodDoc.exists()) {
        setNeighborhoodName(neighborhoodDoc.data().name || userProfile.neighborhoodId)
      }
    } catch (e) {
      console.error('Error loading neighborhood:', e)
    }

    try {
      setLoading(true)
      const announcementsQuery = query(
        collection(db, 'announcements'),
        where('neighborhoodId', '==', userProfile.neighborhoodId),
        orderBy('createdDate', 'desc')
      )

      const snapshot = await getDocs(announcementsQuery)
      const announcementsList = snapshot.docs.map(d => ({
        id: d.id,
        ...d.data(),
        createdDate: d.data().createdDate?.toDate() || new Date()
      }))

      // Pinned first, then newest.
      announcementsList.sort((a, b) => {
        if (a.isPinned && !b.isPinned) return -1
        if (!a.isPinned && b.isPinned) return 1
        return b.createdDate - a.createdDate
      })

      setAnnouncements(announcementsList)

      // Build the { email → profilePhotoURL } map for author avatars.
      try {
        const usersSnap = await getDocs(query(
          collection(db, 'users'),
          where('neighborhoodId', '==', userProfile.neighborhoodId)
        ))
        const map = {}
        usersSnap.docs.forEach(u => {
          const data = u.data() || {}
          if (data.email && data.profilePhotoURL) {
            map[data.email] = data.profilePhotoURL
          }
        })
        setAuthorPhotos(map)
      } catch (e) {
        console.warn('Could not load author photos:', e)
      }
    } catch (error) {
      console.error('Error loading announcements:', error)
    } finally {
      setLoading(false)
    }
  }

  const { settings } = useAppSettings()
  const formatDate = (date) => fmtDate(date, settings, { year: 'numeric', month: 'long', day: 'numeric' })
  const formatRelativeTime = (date) => fmtRelative(date, settings)

  const locationLabel = neighborhoodName || userProfile?.neighborhoodId || 'your neighborhood'
  const totalCount = announcements.length

  return (
    <div className="announcements-page">
      {!isSubscriptionActive() && <PaywallModal isAdmin={isAdmin} />}

      {/* Page header — matches DiscussionsPage vocabulary. */}
      <header className="announcements-header">
        <div className="announcements-header-text">
          <h1>Announcements</h1>
          <p className="muted">
            {totalCount === 0
              ? `No announcements in ${locationLabel} yet`
              : `${totalCount} ${totalCount === 1 ? 'update' : 'updates'} from ${locationLabel}`}
          </p>
        </div>
        {canPost && (
          <button
            className="btn-primary announcements-new-btn"
            data-walk="new-announcement-btn"
            onClick={() => setShowTypeSelector(true)}
            aria-label="New announcement"
            title="New announcement"
          >
            <Plus size={22} strokeWidth={2.5} />
          </button>
        )}
      </header>

      <div className="announcements-list">
        {loading ? (
          <div className="announcements-loading">
            <div className="spinner"></div>
            <p className="muted">Loading announcements…</p>
          </div>
        ) : totalCount === 0 ? (
          <div className="card announcements-empty">
            <div className="announcements-empty-icon">
              <Megaphone size={32} />
            </div>
            <h3>No announcements yet</h3>
            <p className="muted">Check back later for updates from {locationLabel}.</p>
            {canPost && (
              <button className="btn-primary" onClick={() => setShowTypeSelector(true)}>
                <Plus size={18} />
                <span>Post the first update</span>
              </button>
            )}
          </div>
        ) : (
          announcements.map((announcement, idx) => (
            <AnnouncementCard
              key={announcement.id}
              announcement={announcement}
              userProfile={userProfile}
              isAdmin={isAdmin}
              onVoteUpdate={loadAnnouncements}
              formatRelativeTime={formatRelativeTime}
              photoURL={authorPhotos[announcement.authorId] || announcement.authorPhotoURL || ''}
              isFirst={idx === 0}
            />
          ))
        )}
      </div>

      {canPost && (
        <button
          className="fab"
          onClick={() => setShowTypeSelector(true)}
          aria-label="New announcement"
        >
          <Plus size={24} />
        </button>
      )}

      {showTypeSelector && (
        <TypeSelectorModal
          onClose={() => setShowTypeSelector(false)}
          onSelect={(type) => {
            setCreateType(type)
            setShowTypeSelector(false)
            setShowCreateModal(true)
          }}
        />
      )}

      {showCreateModal && (
        <CreateAnnouncementModal
          type={createType}
          onClose={() => {
            setShowCreateModal(false)
            setCreateType(null)
          }}
          onCreated={() => {
            setShowCreateModal(false)
            setCreateType(null)
            loadAnnouncements()
          }}
          userProfile={userProfile}
        />
      )}
    </div>
  )
}

// Announcement card. Header row (avatar + author + timestamp + optional
// pinned/poll pills), then title, body preview (3-line clamp), then a
// footer row with meta and the inline poll (when applicable).
function AnnouncementCard({ announcement, userProfile, isAdmin, onVoteUpdate, formatRelativeTime, photoURL, isFirst }) {
  const authorName = announcement.authorName || announcement.authorId || 'Anonymous'
  const initial = (authorName || 'A').charAt(0).toUpperCase()
  const hasPoll = !!announcement.poll

  const expiresAtDate = announcement.poll?.expiresAt
    ? new Date(announcement.poll.expiresAt)
    : null
  const pollClosed = !!(expiresAtDate && expiresAtDate.getTime() <= Date.now())

  return (
    <article
      className={`card announcement-card${announcement.isPinned ? ' pinned' : ''}`}
      data-walk={isFirst ? 'announcement-card-first' : undefined}
    >
      <header className="announcement-card-header">
        <div className="avatar" aria-hidden="true">
          {photoURL ? (
            <img src={photoURL} alt="" onError={(e) => { e.currentTarget.style.display = 'none' }} />
          ) : (
            initial
          )}
        </div>
        <div className="announcement-card-authorline">
          <span className="announcement-card-author">{authorName}</span>
          <span className="announcement-card-time muted">{formatRelativeTime(announcement.createdDate)}</span>
        </div>
        <div className="announcement-card-pills">
          {announcement.isPinned && (
            <span className="badge announcement-pin-badge">
              <Pin size={12} />
              <span>Pinned</span>
            </span>
          )}
          {hasPoll && (
            <span className="badge announcement-poll-badge">
              <BarChart3 size={12} />
              <span>Poll</span>
            </span>
          )}
          {hasPoll && pollClosed && (
            <span className="badge announcement-closed-badge">Closed</span>
          )}
        </div>
      </header>

      <h2 className="announcement-card-title">{announcement.title}</h2>

      {announcement.content && (
        <p
          className="announcement-card-body"
          dangerouslySetInnerHTML={{ __html: renderAnnouncementContent(announcement.content) }}
        />
      )}

      {hasPoll && (
        <PollSection
          announcement={announcement}
          userProfile={userProfile}
          isAdmin={isAdmin}
          onVoteUpdate={onVoteUpdate}
          expiresAtDate={expiresAtDate}
          pollClosed={pollClosed}
        />
      )}
    </article>
  )
}

// Inline poll — pill choice buttons with slim progress bar under each.
function PollSection({ announcement, userProfile, isAdmin, onVoteUpdate, expiresAtDate, pollClosed }) {
  const { poll } = announcement
  const [voting, setVoting] = useState(false)
  const [showVoters, setShowVoters] = useState(false)

  const voterKey = userProfile?.email || userProfile?.uid
  const userVote = voterKey ? poll.votes?.[voterKey] : undefined
  const totalVotes = Object.keys(poll.votes || {}).length

  const { settings: appSettings } = useAppSettings()
  const formatEndDate = (date) => fmtDateTime(date, appSettings)

  const voteCounts = poll.options.reduce((acc, _, index) => {
    acc[index] = Object.values(poll.votes || {}).filter(v => v === index).length
    return acc
  }, {})

  const handleVote = async (optionIndex) => {
    if (!voterKey || voting || pollClosed) return
    try {
      setVoting(true)
      const newVotes = { ...poll.votes }
      if (userVote === optionIndex) {
        delete newVotes[voterKey]
      } else {
        newVotes[voterKey] = optionIndex
      }
      await updateDoc(doc(db, 'announcements', announcement.id), {
        'poll.votes': newVotes,
      })
      onVoteUpdate()
    } catch (err) {
      console.error('Error voting:', err)
    } finally {
      setVoting(false)
    }
  }

  const getVotersForOption = (optionIndex) => {
    if (!poll.votes) return []
    return Object.entries(poll.votes)
      .filter(([_, vote]) => vote === optionIndex)
      .map(([id]) => id)
  }

  return (
    <div className={`poll-section${pollClosed ? ' poll-closed' : ''}`}>
      {pollClosed ? (
        <div className="poll-status-badge poll-status-closed">
          <Lock size={13} />
          <span>Poll closed · ended {formatEndDate(expiresAtDate)}</span>
        </div>
      ) : expiresAtDate ? (
        <div className="poll-status-badge poll-status-open">
          <Clock size={13} />
          <span>Voting closes {formatEndDate(expiresAtDate)}</span>
        </div>
      ) : null}

      <div className="poll-options">
        {poll.options.map((option, index) => {
          const voteCount = voteCounts[index]
          const percentage = totalVotes > 0 ? Math.round((voteCount / totalVotes) * 100) : 0
          const isSelected = userVote === index

          return (
            <button
              key={index}
              type="button"
              className={`poll-option-btn${isSelected ? ' selected' : ''}`}
              onClick={() => handleVote(index)}
              disabled={voting || pollClosed}
            >
              <span className="poll-option-row">
                <span className="poll-option-label">
                  {isSelected && <Check size={14} />}
                  <span>{option}</span>
                </span>
                <span className="poll-option-stats">
                  {percentage}% <span className="muted">· {voteCount}</span>
                </span>
              </span>
              <span className="poll-option-track" aria-hidden="true">
                <span className="poll-option-fill" style={{ width: `${percentage}%` }} />
              </span>
            </button>
          )
        })}
      </div>

      <div className="poll-footer">
        <span className="muted">{totalVotes} total {totalVotes === 1 ? 'vote' : 'votes'}</span>
        {userVote !== undefined && !pollClosed && (
          <span className="muted">Tap your choice again to remove your vote</span>
        )}
      </div>

      {isAdmin && totalVotes > 0 && (
        <div className="poll-admin-section">
          <button
            type="button"
            className="btn-ghost small show-voters-btn"
            onClick={() => setShowVoters(!showVoters)}
          >
            {showVoters ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            {showVoters ? 'Hide voter details' : 'Show voter details (Admin only)'}
          </button>

          {showVoters && (
            <div className="voters-list">
              {poll.options.map((option, index) => {
                const voters = getVotersForOption(index)
                return (
                  <div key={index} className="option-voters">
                    <strong>{option}:</strong>
                    {voters.length > 0 ? (
                      <span className="voter-ids">{voters.length} voter(s)</span>
                    ) : (
                      <span className="muted">No votes</span>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default AnnouncementsPage
